/**
 * `DIALOG.lineType` (W2-07; DESIGN.md §4.21 "Line type", §5.5 "Line types", F§9.5). Phira derives a line's kind
 * from its texture and its text / paint / GIF tracks, so a type change adds and removes those. The dialog lists
 * what the change removes (text keyframes, brush keyframes, GIF progress and "Play automatically" - Phira cannot
 * load GIF progress on a PNG or JPG -, the picture, the HUD element) and what it adds, asks for what the new type
 * needs (picture, GIF, HUD element, text) and refuses a UI mover while the line has notes, with a "Move N notes"
 * action (another line, one undoable step). The change runs `Core.lines.setLineType` as one undoable step.
 */

import { useState, type ReactNode } from 'react';
import type { DialogProps } from '../../app/registry/types';
import { Core } from '../../core/commands';
import type { LineTypeChoice } from '../../core/commands/types';
import type { AssetId, LineId, UiElement } from '../../core/model/types';
import type { MessageRef } from '../../core/util/types';
import { useT } from '../../i18n/useT';
import type { DocStore } from '../../state/types';
import { useTr } from '../../ui/bridge';
import { Button } from '../../ui/Button';
import { Dialog } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import { Select } from '../../ui/Select';
import { useDocValue, useEditorValue, useServicesStore } from './hooks';
import { lineTitle } from './lineActions';
import { m } from './lines.messages';
import {
  buildLineTable,
  imageAssets,
  planTypeChange,
  TYPE_ICONS,
  typeChoiceOf,
  UI_ELEMENT_ICONS,
  UI_ELEMENT_IDS,
  type TypeGain,
  type TypeLoss,
} from './lineModel';
import s from './Lines.module.css';

const CHOICES: readonly { readonly type: LineTypeChoice; readonly full: boolean }[] = [
  { type: 'line', full: false },
  { type: 'text', full: false },
  { type: 'image', full: false },
  { type: 'gif', full: true },
  { type: 'paint', full: true },
  { type: 'uiMover', full: true },
];

const TOPICS = ['lines', 'lineOrder', 'tracks', 'keys', 'notes', 'assets', 'groups'] as const;

function lossText(t: ReturnType<typeof useT>, l: TypeLoss): string {
  switch (l.kind) {
    case 'textTrack':
      return t(m.lossText, { count: l.keys });
    case 'paintTrack':
      return t(m.lossPaint, { count: l.keys });
    case 'gifTrack':
      return t(m.lossGifTrack, { count: l.keys });
    case 'gifAutoLoop':
      return t(m.lossGifAuto);
    case 'texture':
      return t(m.lossTexture, { path: l.path });
    case 'uiElement':
      return t(m.lossElement, { element: l.element });
  }
}

function gainText(t: ReturnType<typeof useT>, g: TypeGain): string {
  switch (g.kind) {
    case 'uiMoverAlpha':
      return t(m.gainAlpha);
    case 'gifAutoPlay':
      return t(m.gainGifAuto);
    case 'textTrack':
      return t(m.gainText);
    case 'paintTrack':
      return t(m.gainPaint);
  }
}

export function LineTypeDialog(p: DialogProps<'lineType'>) {
  const store = useServicesStore(p.services);
  if (store === null || !store.doc.lines.has(p.props.line)) return null;
  return <LineTypeBody {...p} store={store} />;
}

function LineTypeBody(p: DialogProps<'lineType'> & { readonly store: DocStore }) {
  const t = useT();
  const tr = useTr();
  const { store, services } = p;
  const id = p.props.line;
  const table = useDocValue(store, buildLineTable, TOPICS);
  const level = useEditorValue(services, (st) => st.level);
  const info = table.byId.get(id);
  const line = store.doc.lines.get(id);
  const current = info === undefined ? 'line' : typeChoiceOf(info.type);
  const [target, setTarget] = useState<LineTypeChoice>(p.props.target ?? current);
  const images = imageAssets(store.doc, false);
  const gifs = imageAssets(store.doc, true);
  // The caller's choice (LineTypeProps.texture / attachUI, CR-W2-06-1) is pre-selected.
  const [texture, setTexture] = useState<AssetId | null>(p.props.texture ?? line?.texture ?? null);
  const [element, setElement] = useState<UiElement>(p.props.attachUI ?? line?.attachUI ?? 'score');
  const [text, setText] = useState('');
  const [moveTo, setMoveTo] = useState<LineId | null>(null);
  const [error, setError] = useState<MessageRef | null>(null);
  if (line === undefined || info === undefined) return null;

  const pool = target === 'gif' ? gifs : images;
  const chosenTexture = texture !== null && pool.some((a) => a.id === texture) ? texture : (pool[0]?.id ?? null);
  const plan = planTypeChange(store.doc, store.idx, line, target, {
    ...(target === 'image' || target === 'gif' ? { texture: chosenTexture } : {}),
    ...(target === 'uiMover' ? { element } : {}),
  });
  const name = lineTitle(store, id);
  const otherLines = table.order.filter((l) => {
    const o = table.byId.get(l);
    return l !== id && o !== undefined && o.uiElement === null;
  });
  const moveTarget = moveTo !== null && otherLines.includes(moveTo) ? moveTo : (otherLines[0] ?? null);

  const close = (): void => {
    p.close(null);
  };

  const moveNotes = (): void => {
    if (moveTarget === null) return;
    const ids = [...store.idx.notesByLine(id)];
    const out = store.transact(
      {
        label: { key: m.txMoveNotes, params: { count: ids.length, line: lineTitle(store, moveTarget) } },
        command: 'lines.changeType',
      },
      (tx) => {
        Core.notes.moveNotesToLine(tx, { ids, line: moveTarget });
      },
    );
    if (!out.ok) setError(out.reason);
    else {
      setError(null);
      services.ui.toast(
        { key: m.notesMoved, params: { count: ids.length, line: lineTitle(store, moveTarget) } },
        { kind: 'success', undo: true },
      );
    }
  };

  const apply = (): void => {
    if (plan.unchanged) {
      p.close(null);
      return;
    }
    const out = store.transact(
      {
        label: { key: m.txChangeType, params: { type: target, line: name } },
        command: 'lines.changeType',
        focus: { line: id },
      },
      (tx) => {
        Core.lines.setLineType(tx, {
          line: id,
          type: target,
          ...((target === 'image' || target === 'gif') && chosenTexture !== null ? { texture: chosenTexture } : {}),
          ...(target === 'uiMover' ? { attachUI: element } : {}),
          ...(target === 'text' && text.trim() !== '' ? { text } : {}),
        });
      },
    );
    if (!out.ok) {
      setError(out.reason);
      return;
    }
    for (const note of out.notes) services.ui.toast(note, { kind: 'info' });
    services.ui.toast({ key: m.typeChanged, params: { line: name, type: target } }, { kind: 'success', undo: true });
    p.close({ applied: true });
  };

  const blocked = plan.blockedByNotes > 0 || plan.needsTexture || plan.needsElement;
  let needs: ReactNode = null;
  if (target === 'image' || target === 'gif') {
    needs =
      pool.length === 0 ? (
        <div className={s.warning}>
          <Icon name="ImageOff" size={16} />
          <span>{t(target === 'gif' ? m.noGifs : m.noImages)}</span>
          <Button
            size="sm"
            icon="ImagePlus"
            onClick={() => {
              void services.registry.run('assets.add', { role: target === 'gif' ? 'gif' : 'texture' });
            }}
          >
            {t(m.addPicture)}
          </Button>
        </div>
      ) : (
        <Select<string>
          label={t(target === 'gif' ? m.pickGif : m.pickImage)}
          value={chosenTexture ?? (pool[0]?.id as string)}
          options={pool.map((a) => ({ value: a.id, label: a.path, icon: a.meta?.codec === 'gif' ? 'Film' : 'Image' }))}
          onChange={(v) => {
            setTexture(v as AssetId);
          }}
        />
      );
  } else if (target === 'uiMover') {
    needs = (
      <Select<UiElement>
        label={t(m.pickElement)}
        value={element}
        options={UI_ELEMENT_IDS.map((e) => ({
          value: e,
          label: t(m.uiElementName, { element: e }),
          icon: UI_ELEMENT_ICONS[e],
        }))}
        onChange={(v) => {
          setElement(v);
        }}
      />
    );
  } else if (target === 'text' && plan.gains.some((g) => g.kind === 'textTrack')) {
    needs = (
      <div className={s.fieldRow}>
        <label className={s.label} htmlFor="lines-type-text">
          {t(m.initialText)}
        </label>
        <input
          id="lines-type-text"
          className={s.input}
          value={text}
          placeholder={t(m.initialTextPlaceholder)}
          onChange={(e) => {
            setText(e.target.value);
          }}
        />
      </div>
    );
  }

  return (
    <Dialog
      open={true}
      size="md"
      title={t(m.typeTitle, { line: name })}
      description={t(m.typeDescription)}
      onClose={close}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            {t(m.cancel)}
          </Button>
          <Button
            variant={plan.losses.length > 0 ? 'danger' : 'primary'}
            icon="Shapes"
            disabledReason={blocked ? { key: m.typeBlocked } : plan.unchanged ? { key: m.typeUnchanged } : null}
            onClick={apply}
          >
            {t(m.typeApply, { type: target })}
          </Button>
        </>
      }
    >
      <div className={s.form}>
        <fieldset className={s.choices}>
          <legend className={s.label}>{t(m.typeCurrent, { type: current })}</legend>
          <div className={s.typeGrid}>
            {CHOICES.filter((c) => !c.full || level === 'full' || c.type === current || c.type === target).map((c) => (
              <label key={c.type} className={s.choice}>
                <input
                  type="radio"
                  name="lineType"
                  value={c.type}
                  checked={target === c.type}
                  onChange={() => {
                    setTarget(c.type);
                    setError(null);
                  }}
                />
                <span className={s.choiceTitle}>
                  <Icon name={c.type === 'line' ? TYPE_ICONS.line : TYPE_ICONS[c.type]} size={14} />{' '}
                  {t(m.typeName, { type: c.type })}
                </span>
                <span className={s.choiceText}>{t(m.typeHelp, { type: c.type })}</span>
              </label>
            ))}
          </div>
        </fieldset>
        {needs}
        {plan.blockedByNotes > 0 && (
          <div className={s.warning} role="alert">
            <Icon name="TriangleAlert" size={16} />
            <div className={s.form}>
              <span>{t(m.uiMoverNotes, { count: plan.blockedByNotes, line: name })}</span>
              {moveTarget === null ? (
                <span>{t(m.noOtherLine)}</span>
              ) : (
                <div className={s.newGroup}>
                  <Select<string>
                    label={t(m.moveNotesTo)}
                    value={String(moveTarget)}
                    options={otherLines.map((l) => ({ value: String(l), label: lineTitle(store, l) }))}
                    onChange={(v) => {
                      setMoveTo(Number(v) as LineId);
                    }}
                  />
                  <Button size="sm" icon="MoveRight" onClick={moveNotes}>
                    {t(m.moveNotes, { count: plan.blockedByNotes })}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        {plan.losses.length > 0 && (
          <div className={s.warning}>
            <Icon name="TriangleAlert" size={16} />
            <div>
              <p>{t(m.lossIntro)}</p>
              <ul className={s.list}>
                {plan.losses.map((l) => (
                  <li key={l.kind}>{lossText(t, l)}</li>
                ))}
              </ul>
              <p className={s.help}>{t(m.lossUndo)}</p>
            </div>
          </div>
        )}
        {plan.gains.length > 0 && (
          <div className={s.info}>
            <Icon name="Info" size={16} />
            <ul className={s.list}>
              {plan.gains.map((g) => (
                <li key={g.kind}>{gainText(t, g)}</li>
              ))}
            </ul>
          </div>
        )}
        {error !== null && (
          <div className={s.error} role="alert">
            <Icon name="CircleX" size={16} />
            <span>{tr(error)}</span>
          </div>
        )}
      </div>
    </Dialog>
  );
}
